/**
 * Tag Service
 * API calls for tag operations
 */

import { Effect } from "effect";
import type { TagsListResponse, Tag, Link, TagResponse } from "@link-storage/contracts";
import { fromTagResponse, fromLinkResponse } from "@link-storage/contracts";
import type { AppError } from "@link-storage/common";
import { get, put } from "./apiClient.js";

/**
 * Get all tags
 */
export const getAllTags = (): Effect.Effect<ReadonlyArray<Tag>, AppError> =>
  Effect.gen(function* (_) {
    const response = yield* _(get<TagsListResponse>("/tags"));
    return response.tags.map(fromTagResponse);
  });

/**
 * Search tags by name
 */
export const searchTags = (query: string): Effect.Effect<ReadonlyArray<Tag>, AppError> =>
  Effect.gen(function* (_) {
    const response = yield* _(get<TagsListResponse>(`/tags/search?q=${encodeURIComponent(query)}`));
    return response.tags.map(fromTagResponse);
  });

/**
 * Update link tags
 */
export const updateLinkTags = (
  linkId: string,
  tags: ReadonlyArray<string>
): Effect.Effect<Link, AppError> =>
  Effect.gen(function* (_) {
    const response = yield* _(
      put<{ data: { id: string; url: string; title: string | null; createdAt: string; updatedAt: string; ogMetadata: { title: string | null; description: string | null; image: string | null; status: "pending" | "success" | "failed"; fetchedAt: string | null; error: string | null }; tags: TagResponse[] }; error: null }>(
        `/links/${linkId}/tags`,
        { tags }
      )
    );
    return fromLinkResponse(response.data);
  });

/**
 * Get links by tag
 */
export const getLinksByTag = (tagName: string): Effect.Effect<ReadonlyArray<Link>, AppError> =>
  Effect.gen(function* (_) {
    const response = yield* _(get<{ links: Array<{ id: string; url: string; title: string | null; createdAt: string; updatedAt: string; ogMetadata: { title: string | null; description: string | null; image: string | null; status: "pending" | "success" | "failed"; fetchedAt: string | null; error: string | null }; tags: TagResponse[] }>; total: number }>(`/links?tag=${encodeURIComponent(tagName)}`));
    return response.links.map(fromLinkResponse);
  });
